import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DescriptionIcon from "@mui/icons-material/Description";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import PaymentIcon from "@mui/icons-material/Payment";
import PeopleIcon from "@mui/icons-material/People";
import PlaceIcon from "@mui/icons-material/Place";
import TodayIcon from "@mui/icons-material/Today";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  ChipProps,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import { genProductPriceLabel } from "mt-shared/genProductPriceLabel";
import { BasicGoogleMapsQuery, generateGoogleMapsUrl } from "mt-shared/maps";
import { requiresTransportAgreement } from "mt-shared/transport";
import { useState } from "react";
import { BiPencil } from "react-icons/bi";
import { GetBookingsQuery } from "../../../__generated__/graphql";
import EditPickupLocation from "../../../components/PickupLocation/EditPickupLocation";
import pickupStyles from "../../../components/PickupLocation/PickupLocation.module.css";
import CancelBooking from "./Cancel";
import BookingForm from "./Form";
import styles from "./Booking.module.scss";

export type BookingData = GetBookingsQuery["bookings"]["records"][number] & {
  group: {
    product: {
      requiresTransportAgreement?: boolean | null;
      hasAccomodationVehiclePickup?: boolean;
      hasDefaultVehiclePickup?: boolean;
    };
  };
};

export default function Booking(props: {
  data: BookingData;
  onOpenTsa: () => void;
}) {
  const booking = props.data;
  const tourTimezone = booking.group.product.location.timezone || "";
  const reservationDateTime = DateTime.fromFormat(
    `${booking.group.date} ${booking.group.time}`,
    "yyyy-MM-dd HH:mm",
    { zone: tourTimezone },
  );
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const guestNames = (booking.guests ?? [])
    .map(({ givenName, familyName }) => `${givenName} ${familyName}`.trim())
    .filter(Boolean);
  const createdAt = DateTime.fromISO(booking.created, { zone: "Asia/Tokyo" });
  const attendance =
    booking.didAttend === null ? "Unknown" : booking.didAttend ? "Yes" : "No";
  const showAttendance =
    booking.state === "CONFIRMED" &&
    reservationDateTime.isValid &&
    reservationDateTime.startOf("day").toMillis() <=
      DateTime.now().setZone(tourTimezone).startOf("day").toMillis();
  const canEditBookingDetails =
    reservationDateTime.isValid &&
    DateTime.now().setZone(tourTimezone) < reservationDateTime.plus({ days: 7 });

  return (
    <Grid item xs={12} sm={6}>
      <Card variant="outlined" className={styles.bookingCard}>
        <CardContent className={styles.cardContent}>
          <Stack spacing={2}>
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                spacing={1}
              >
                <Typography className={styles.bookingReference}>
                  {booking.id}
                  {booking.sourceBookingId && (
                    <> (ref: {booking.sourceBookingId})</>
                  )}
                </Typography>
                <Chip
                  color={getChipColor(booking.state)}
                  label={booking.state}
                  size="small"
                  className={styles.statusChip}
                />
              </Stack>
              <Typography component="h2" className={styles.guestNames}>
                {guestNames.join(", ") || "Guest name unavailable"}
              </Typography>
              {!booking.salesPartnerId && (
                <Chip
                  label="Booked outside partner portal"
                  size="small"
                  className={styles.sourceChip}
                />
              )}
            </Box>

            <Stack className={styles.bookingDetails} spacing={1.1}>
              <Typography className={styles.tourName}>
                {booking.group.product.translation?.name || "UNKNOWN TOUR"}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                className={styles.detailRow}
              >
                <TodayIcon className={styles.detailIcon} />
                <span>
                  {reservationDateTime.toFormat(
                    "cccc LLLL d yyyy, HH:mm (ZZZZ)",
                  )}
                </span>
              </Stack>
              <Stack direction="row" spacing={1.25} alignItems="flex-start">
                <PeopleIcon
                  className={`${styles.detailIcon} ${styles.peopleIcon}`}
                />
                <Box>
                  {booking.details.map((detail, index) => (
                    <div key={index}>
                      {detail.quantity}x{" "}
                      {genProductPriceLabel(detail.productPrice)}
                    </div>
                  ))}
                </Box>
              </Stack>
              {booking.group.product.availabilityType === "PRIVATE" && (
                <div>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <PlaceIcon className={styles.detailIcon} />
                    {booking.pickupLocation?.query?.geoLocation ? (
                      <div>
                        <a
                          className={styles.pickupLink}
                          href={generateGoogleMapsUrl(
                            booking.pickupLocation
                              .query as BasicGoogleMapsQuery,
                          )}
                          target="_blank"
                        >
                          {booking.pickupLocation.name}
                          <br />
                          {booking.pickupLocation.address}
                        </a>
                        <span
                          className={pickupStyles.EditBtn}
                          onClick={() => setOpenEditDialog(true)}
                        >
                          <BiPencil className={pickupStyles.EditBtnIcon} />
                          Edit
                        </span>
                      </div>
                    ) : (
                      <div>
                        <span>Pickup not set</span>
                        <span
                          className={pickupStyles.EditBtn}
                          onClick={() => setOpenEditDialog(true)}
                        >
                          <BiPencil className={pickupStyles.EditBtnIcon} />
                          Edit
                        </span>
                      </div>
                    )}
                  </Stack>
                </div>
              )}
              {showAttendance && (
                <Stack
                  direction="row"
                  alignItems="center"
                  className={styles.attendanceRow}
                >
                  {booking.didAttend === null ? (
                    <HelpOutlineIcon className={styles.attendanceIcon} />
                  ) : booking.didAttend ? (
                    <CheckCircleOutlineIcon className={styles.attendanceIcon} />
                  ) : (
                    <HighlightOffIcon className={styles.attendanceIcon} />
                  )}
                  <Typography className={styles.attendanceLabel}>
                    Attended:
                  </Typography>
                  <Typography
                    className={
                      booking.didAttend === null
                        ? styles.attendanceUnknown
                        : booking.didAttend
                          ? styles.attendanceYes
                          : styles.attendanceNo
                    }
                  >
                    {attendance}
                  </Typography>
                </Stack>
              )}
            </Stack>

            <Divider className={styles.divider} />
            <BookingForm
              bookingId={booking.id}
              disabled={!canEditBookingDetails}
              email={booking.email}
              phoneNumber={booking.phoneNumber}
              specialRequests={booking.specialRequests}
            />
            <Stack direction="row" spacing={1.25} alignItems="center">
              <PaymentIcon className={styles.detailIcon} />
              <Typography className={styles.paymentAmount}>
                {Intl.NumberFormat("ja-JP", {
                  style: "currency",
                  currency: booking.payment?.currency,
                }).format(booking.payment?.amount || 0)}
              </Typography>
            </Stack>
            <Stack className={styles.metadata} spacing={0.7}>
              <Typography className={styles.metadataText}>
                <b>Created:</b>{" "}
                {createdAt.isValid
                  ? createdAt.toFormat("yyyy-MM-dd HH:mm")
                  : booking.created}{" "}
                (GMT+9)
              </Typography>
              {booking.state === "CANCELLED" && (
                <>
                  <Typography className={styles.metadataText}>
                    <b>Cancelled By:</b> {booking.cancelledBy || "—"}
                  </Typography>
                  <Typography className={styles.cancellationReason}>
                    <b>Cancellation Reason:</b>{" "}
                    {booking.cancellationReason || "—"}
                  </Typography>
                </>
              )}
            </Stack>
          </Stack>
        </CardContent>
        {booking.state === "CONFIRMED" && (
          <CardActions className={styles.cardActions}>
            {requiresTransportAgreement(booking.group.product) && (
              <Stack
                component="button"
                direction="row"
                spacing={1}
                alignItems="center"
                onClick={props.onOpenTsa}
                className={styles.tsaButton}
              >
                <DescriptionIcon className={styles.tsaIcon} />
                <Typography className={styles.tsaLabel}>
                  Transport Service Agreement
                </Typography>
              </Stack>
            )}
            <CancelBooking booking={booking} />
          </CardActions>
        )}
      </Card>
      <EditPickupLocation
        open={openEditDialog}
        handleClose={() => setOpenEditDialog(false)}
        booking={booking}
      />
    </Grid>
  );
}

function getChipColor(state: string): ChipProps["color"] {
  if (state === "CONFIRMED") return "success";
  if (state === "CANCELLED") return "error";
  return "default";
}
